// Aggregate raw GeoNet quakes into 7 daily "diary entries".
// Each entry captures the restlessness of the dog on that day: how many
// felt tremors happened, their peak magnitude, and a per-hour intensity
// sparkline. A short annotation is generated from the restlessness score.

const DAY_MS = 86_400_000;

// Format YYYY-MM-DD in local time.
const dayKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const restlessLabel = (score, maxMag) => {
  if (maxMag != null && maxMag >= 6) {
    return {
      tone: "wake",
      title: "Startled awake",
      body: "He shot upright near his ear side, barked once, then sat listening long after the ground was still.",
    };
  }
  if (score >= 82) {
    return {
      tone: "restless",
      title: "Restless night",
      body: "He circled the mat three times and could not find the shape he wanted.",
    };
  }
  if (score >= 55) {
    return {
      tone: "twitching",
      title: "Twitching paws",
      body: "Ears flicked at every distant murmur. His dreams were noisy but he kept them closed.",
    };
  }
  if (score >= 28) {
    return {
      tone: "sigh",
      title: "One long sigh",
      body: "A soft breath at first light. Whatever passed beneath him was already gone.",
    };
  }
  if (score > 0) {
    return {
      tone: "still",
      title: "Barely a whisper",
      body: "The ground kept its voice low. He barely moved a whisker.",
    };
  }
  return {
    tone: "deep",
    title: "Deep, dreamless sleep",
    body: "Not so much as a twitch. The earth held its breath, and so did he.",
  };
};

export const aggregateWeek = (quakes, options = {}) => {
  // Options: { startDate: Date } for a specific 7-day window, or
  // { endDate: Date } for a rolling week ending at endDate. Defaults to
  // the current week ending today.
  const days = [];
  let start;
  if (options.startDate) {
    start = new Date(options.startDate);
    start.setHours(0, 0, 0, 0);
  } else {
    const end = new Date(options.endDate || new Date());
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  }

  for (let i = 0; i < 7; i++) {
    const d = new Date(start.getTime() + i * DAY_MS);
    days.push({
      date: d,
      key: dayKey(d),
      count: 0,
      maxMag: null,
      peakHour: null,
      hourly: new Array(24).fill(0),
      energy: 0,
    });
  }
  const byKey = Object.fromEntries(days.map((d) => [d.key, d]));

  for (const q of quakes) {
    if (!q.time || q.magnitude == null) continue;
    const t = new Date(q.time);
    const k = dayKey(t);
    const day = byKey[k];
    if (!day) continue;
    day.count += 1;
    // Energy = pow(mag, 1.5); day energy is sum
    const e = Math.pow(Math.max(0, q.magnitude), 1.5);
    day.energy += e;
    day.hourly[t.getHours()] += e;
    if (day.maxMag == null || q.magnitude > day.maxMag) {
      day.maxMag = q.magnitude;
      day.peakHour = t.getHours();
    }
  }

  // Normalise restlessness to 0-100 across the visible week so bars/scores
  // stay meaningful even in quiet stretches.
  const maxEnergy = Math.max(1, ...days.map((d) => d.energy));
  for (const d of days) {
    d.restlessness = Math.round((d.energy / maxEnergy) * 100);
    d.annotation = restlessLabel(d.restlessness, d.maxMag);
  }
  return days;
};

export const weekdayShort = (d) =>
  d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
export const dayNumber = (d) => d.getDate();
export const monthShort = (d) =>
  d.toLocaleDateString(undefined, { month: "short" });
